import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { Button } from "@/components/ui/button";
import { breadcrumbSchema, faqSchema, jsonLd, absUrl } from "@/lib/seo";
import { GENERIC_BOKADIREKT } from "@/lib/booking";
import { priceRows } from "@/data/pricing";

const BOOK_URL = GENERIC_BOKADIREKT;

const klippning = priceRows([
  "Herrklippning",
  "Skin Fade / Taper Fade",
  "Barnklippning",
  "Seniorklippning 65+",
  "Signature Cut",
]);

const skagg = priceRows([
  "Klippning & Skäggtrimning",
  "Skäggtrimning & Snagg",
  "Skäggtrimning",
  "Färgning Skägg",
  "Slätrakning",
  "Klippning & Slätrakning",
]);

const behandlingar = priceRows([
  "Färgning Skägg & Skäggtrimning",
  "Färgning med klipp",
  "Endast Färgning",
]);

const annat = priceRows([
  "Vaxning Näsa & Öron",
  "Öronljusbehandling",
  "Ögonbrynsfärgning för män",
  "Mjäll- & Torr Hårbottenbehandling",
]);

const faq = [
  { q: "Vad kostar en herrklippning hos Tailor's Barber?", a: "450 kr. Inkluderar konsultation, klippning och styling." },
  { q: "Vad kostar skäggtrimning?", a: "380 kr." },
  { q: "Vad kostar en kombinationsbehandling med klippning och skägg?", a: "680 kr." },
  { q: "Vad kostar barnklippning?", a: "380 kr för barn upp till 10 år." },
  { q: "Vad kostar seniorklippning?", a: "270 kr, tillgänglig mån–tors 10–14." },
  { q: "Varierar priset beroende på salong?", a: "Nej, priset är samma på alla fyra salonger." },
  { q: "Finns det rabatter?", a: "Nya kunder erbjuds 15% rabatt vid Åhléns City. Seniorklippning 270 kr." },
];

function PriceTable({ heading, rows }: { heading: string; rows: string[][] }) {
  return (
    <section className="container-page py-8">
      <h2 className="text-2xl md:text-3xl mb-5">{heading}</h2>
      <div className="card-elev overflow-hidden">
        <table className="w-full text-left">
          <tbody>
            {rows.map(([name, price], i) => (
              <tr key={i} className={i > 0 ? "border-t border-border/60" : ""}>
                <td className="px-5 py-4 text-base md:text-lg">{name}</td>
                <td className="px-5 py-4 text-base md:text-lg text-right font-medium whitespace-nowrap">{price}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export const Route = createFileRoute("/priser")({
  head: () => ({
    meta: [
      { title: "Priser – Herrklippning & skäggvård | Tailor's Barber" },
      { name: "description", content: "Prislista för Tailor's Barber i Stockholm. Herrklippning 450 kr, skäggtrimning 380 kr, klippning & skägg 680 kr. Se alla priser." },
      { property: "og:title", content: "Priser – Tailor's Barber" },
      { property: "og:description", content: "Prislista för Tailor's Barber i Stockholm." },
      { property: "og:url", content: absUrl("/priser") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: absUrl("/priser") }],
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Hem", url: "/" }, { name: "Priser", url: "/priser" }])),
      jsonLd(faqSchema(faq)),
    ],
  }),
  component: PriserPage,
});

function PriserPage() {
  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Priser</span>
        </nav>
        <div className="eyebrow">Prislista</div>
        <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
          Priser hos Tailor&apos;s Barber
        </h1>
        <p className="mt-5 text-lg text-foreground/80 max-w-prose leading-relaxed">
          Samma priser på alla fyra salonger i Stockholm. Boka enkelt online eller kom förbi på drop-in.
        </p>
        <div className="mt-6">
          <Button asChild size="lg">
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka din tid</a>
          </Button>
        </div>
      </section>

      <PriceTable heading="Klippning" rows={klippning} />
      <PriceTable heading="Skägg" rows={skagg} />
      <PriceTable heading="Behandlingar" rows={behandlingar} />
      <PriceTable heading="Annat" rows={annat} />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Vad ingår i priset?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          En herrklippning hos Tailor&apos;s Barber (450 kr) inkluderar alltid: konsultation om önskad
          stil, klippning med sax och/eller maskin, formning av nacke och sidor, line-ups och konturer
          samt styling med Tailor&apos;s Grooming-produkter. Du får också tips om hur du sköter frisyren
          hemma.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Hur ska man tänka kring pris på barberare?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Se barberarbesöket som en återkommande investering. En professionell klippning håller formen
          längre, växer ut snyggt och gör vardagsstylingen enklare. För herrklippning i Stockholm ligger
          priset generellt på 400–600 kr. Hos Tailor&apos;s Barber håller vi 450 kr med hög och
          konsekvent kvalitet.
        </p>
      </section>

      <Faq items={faq} heading="Vanliga frågor om priser" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Redo att boka?</h2>
          <p className="mt-3 text-foreground/80">Välj tid, salong och barberare direkt online.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka din tid</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
